import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Send, Paperclip, X, ChevronRight, Inbox, Check, Heart,
  Bug, Lightbulb, MessageCircle, HelpCircle, Wand2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MESSAGE_TYPES = [
  { key: "suggestion",  label: "Suggestion",   icon: Lightbulb },
  { key: "bug_report",  label: "Bug report",   icon: Bug },
  { key: "ux_feedback", label: "UX feedback",  icon: Wand2 },
  { key: "ai_feedback", label: "AI feedback",  icon: Sparkles },
  { key: "question",    label: "Question",     icon: HelpCircle },
  { key: "other",       label: "Other",        icon: MessageCircle },
];

const FEEDBACK_TAGS = [
  "ontology_concern", "tone_concern", "ux_concern",
  "recommendation_concern", "feature_request",
  "continuity_concern", "emotional_realism_concern",
  "performance_concern", "design_concern", "wording_concern", "safety_concern",
];

const ALLOWED_MIMES = {
  "image/png": ".png", "image/jpeg": ".jpg", "image/webp": ".webp",
  "image/gif": ".gif", "application/pdf": ".pdf", "text/plain": ".txt",
};
const MAX_BYTES = 10 * 1024 * 1024;

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    open:            "bg-mauve/15 text-mauve",
    replied:         "bg-sage/15 text-sage-dark",
    waiting_on_user: "bg-champagne text-cocoa",
    resolved:        "bg-cocoa/10 text-cocoa",
    archived:        "bg-ivory text-cocoa/50",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${map[status] || ""}`}>
      {(status || "").replaceAll("_", " ")}
    </span>
  );
}

function SubmitForm({ onSubmitted }) {
  const [type, setType] = useState("suggestion");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState([]);
  const [files, setFiles] = useState([]);  // [{filename, mime, content_b64, size_bytes}]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleTag = (t) => setTags((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const addFiles = async (fileList) => {
    setErr("");
    const incoming = Array.from(fileList || []);
    const remaining = 3 - files.length;
    if (remaining <= 0) { setErr("Maximum 3 attachments."); return; }
    const accepted = [];
    for (const f of incoming.slice(0, remaining)) {
      if (!ALLOWED_MIMES[f.type]) { setErr(`File type not allowed: ${f.type || f.name}`); continue; }
      if (f.size > MAX_BYTES) { setErr(`${f.name} is larger than 10 MB`); continue; }
      try {
        const b64 = await readFileAsBase64(f);
        accepted.push({ filename: f.name, mime: f.type, content_b64: b64, size_bytes: f.size });
      } catch { setErr(`Couldn't read ${f.name}`); }
    }
    setFiles((s) => [...s, ...accepted]);
  };
  const removeFile = (i) => setFiles((s) => s.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (title.length < 2 || body.length < 2) { setErr("Title and body required."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/ambassador/messages", {
        message_type: type, title, body,
        feedback_tags: tags,
        attachments: files.map(({ filename, mime, content_b64 }) => ({ filename, mime, content_b64 })),
      });
      setType("suggestion"); setTitle(""); setBody(""); setTags([]); setFiles([]);
      onSubmitted?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="amb-submit-form" className="ss-card-premium p-6">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Share what's on your mind</p>
      <h3 className="font-heading text-2xl text-cocoa mt-1">A note to the team.</h3>

      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mt-5">Type</p>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="amb-type-row">
        {MESSAGE_TYPES.map((t) => {
          const Icon = t.icon;
          const active = type === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              data-testid={`amb-type-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[12px] border transition-all ${
                active ? "bg-mauve/15 border-mauve text-cocoa" : "bg-ivory border-champagne text-cocoa/70 hover:border-mauve/60"
              }`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mt-5">Title</p>
      <input
        value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
        placeholder="One line — what is this about?"
        data-testid="amb-title"
        className="mt-1.5 w-full bg-ivory border border-champagne rounded-md h-11 px-3 text-cocoa focus:border-mauve outline-none"
      />

      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mt-4">Message</p>
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} rows={5}
        placeholder="Tell us what you noticed, suggested, or experienced."
        data-testid="amb-body"
        className="mt-1.5 w-full bg-ivory border border-champagne rounded-md p-3 text-cocoa focus:border-mauve outline-none leading-relaxed"
      />

      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mt-4">Tag this (optional)</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {FEEDBACK_TAGS.map((t) => (
          <button
            key={t}
            onClick={() => toggleTag(t)}
            data-testid={`amb-tag-${t}`}
            className={`px-3 h-7 rounded-full text-[11px] border transition-all ${
              tags.includes(t)
                ? "bg-mauve/15 border-mauve text-cocoa"
                : "bg-ivory border-champagne text-cocoa/65 hover:border-mauve/60"
            }`}
          >{t.replaceAll("_", " ")}</button>
        ))}
      </div>

      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mt-4">Attachments (optional · max 3 · 10 MB each)</p>
      <label
        data-testid="amb-attach-label"
        className="mt-2 inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[12px] bg-ivory border border-champagne text-cocoa/70 hover:border-mauve/60 cursor-pointer"
      >
        <Paperclip size={12} /> Add file
        <input
          type="file"
          accept={Object.keys(ALLOWED_MIMES).join(",")}
          multiple
          onChange={(e) => addFiles(e.target.files)}
          className="hidden"
          data-testid="amb-attach-input"
        />
      </label>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-ivory border border-champagne rounded-md px-3 py-2 text-[12px]">
              <span className="text-cocoa truncate mr-2">{f.filename} <span className="text-cocoa/45">· {Math.round(f.size_bytes/1024)} KB</span></span>
              <button onClick={() => removeFile(i)} className="text-cocoa/50 hover:text-rose"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}

      {err && <p className="mt-3 text-rose text-[12px]" data-testid="amb-err">{err}</p>}

      <div className="mt-5 flex justify-end border-t border-champagne pt-4">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="amb-submit-btn"
          className="ss-pill ss-pill-primary h-10 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? "Sending…" : (<><Send size={14} /> Send</>)}
        </button>
      </div>
    </div>
  );
}


function MessageThread({ msg, onChanged }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (reply.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/ambassador/messages/${msg.id}/reply`, { body: reply });
      setReply("");
      onChanged?.();
    } catch { /* soft fail */ }
    finally { setBusy(false); }
  };

  return (
    <div className="ss-card-premium p-6 mt-4" data-testid="amb-thread">
      <div className="flex justify-between flex-wrap gap-3 items-start">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{msg.message_type.replaceAll("_", " ")}</p>
          <p className="font-heading text-xl text-cocoa mt-1">{msg.title}</p>
        </div>
        <StatusPill status={msg.status} />
      </div>
      <p className="text-[11px] text-cocoa/55 font-mono mt-1">{formatDate(msg.created_at)}</p>
      <p className="text-cocoa/85 text-[14px] leading-relaxed mt-3 whitespace-pre-wrap">{msg.body}</p>

      {msg.attachments?.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Attachments</p>
          <ul className="mt-1 space-y-1">
            {msg.attachments.map((a) => (
              <li key={a.id} className="text-[12px] text-cocoa/75">
                {a.filename} <span className="text-cocoa/45">· {Math.round(a.size_bytes/1024)} KB</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {msg.thread?.length > 0 && (
        <div className="mt-5 border-t border-champagne pt-4 space-y-3">
          {msg.thread.map((t) => (
            <div key={t.id} className={`rounded-md p-3 border ${t.author_role === "admin" ? "bg-mauve/5 border-mauve/30" : "bg-champagne/20 border-champagne"}`}>
              <p className="text-[10px] tracking-[0.3em] uppercase mb-1 text-cocoa/55">
                {t.author_role === "admin" ? "Team reply" : "You"} · {formatDate(t.created_at)}
              </p>
              <p className="text-[13px] text-cocoa whitespace-pre-wrap">{t.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 border-t border-champagne pt-4">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply…"
          rows={2}
          data-testid="amb-reply-input"
          className="w-full bg-ivory border border-champagne rounded-md p-3 text-[13px] text-cocoa focus:border-mauve outline-none"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={send}
            disabled={busy || reply.trim().length < 2}
            data-testid="amb-reply-btn"
            className="ss-pill ss-pill-primary h-9 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send size={12} /> Reply
          </button>
        </div>
      </div>
    </div>
  );
}


function AppreciationStrip({ overview }) {
  if (!overview) return null;
  return (
    <div
      className="ss-card-premium p-6 sm:p-7"
      style={{ background: "linear-gradient(135deg, rgba(247,237,229,0.7), rgba(255,251,247,0.95))" }}
    >
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Ambassador program</p>
      <h1 className="font-heading text-3xl text-cocoa mt-1">Thank you for shaping SugarSense.</h1>
      <p className="text-cocoa/75 text-sm mt-2 leading-relaxed max-w-2xl">
        You're part of a small group helping us refine the platform. Your feedback directly
        improves the AI's emotional intelligence, the experience members have here, and the
        product's long-term integrity. We're grateful you're with us.
      </p>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Access" value={(overview.access_level || "luxe").toUpperCase()} />
        <Tile label="Evaluation ends" value={formatDate(overview.evaluation_end_date)} />
        <Tile label="Days remaining" value={overview.days_remaining ?? "—"} accent="text-mauve" />
        <Tile label="Lifetime discount after" value={`${overview.future_discount_percent}%`} accent="text-sage-dark" />
      </div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="bg-ivory border border-champagne rounded-md p-4">
      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
      <p className={`font-mono text-xl mt-1.5 ${accent || "text-cocoa"}`}>{value}</p>
    </div>
  );
}


export default function AmbassadorPortal() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeMsg, setActiveMsg] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    api.get("/ambassador/me").then((r) => setOverview(r.data?.ambassador)).catch(() => setOverview(null));
  }, [refreshTick]);

  useEffect(() => {
    api.get("/ambassador/messages")
      .then((r) => setMessages(r.data?.items || []))
      .catch(() => setMessages([]));
  }, [refreshTick]);

  useEffect(() => {
    if (!activeMsg?.id) return;
    api.get(`/ambassador/messages/${activeMsg.id}`)
      .then((r) => setActiveMsg(r.data))
      .catch(() => {});
  }, [activeMsg?.id, refreshTick]);

  const visibleMessages = useMemo(() => messages, [messages]);

  if (!user) return null;
  if (user && !overview?.is_ambassador && overview !== null) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <p className="text-cocoa/75">This page is for SugarSense ambassadors.</p>
      </div>
    );
  }

  return (
    <div data-testid="ambassador-portal" className="px-4 sm:px-6 py-8 max-w-5xl mx-auto space-y-6">
      <AppreciationStrip overview={overview} />

      {overview?.expired && (
        <div className="ss-card-premium p-6 border border-rose/30" data-testid="amb-expired-banner">
          <p className="text-[11px] tracking-[0.4em] uppercase text-rose">Evaluation complete</p>
          <h3 className="font-heading text-2xl text-cocoa mt-1">Thank you for everything.</h3>
          <p className="text-cocoa/75 text-sm mt-2 max-w-2xl">
            Your six months helping us shape SugarSense are complete. Your lifetime
            ambassador discount of <span className="font-medium text-cocoa">{overview.future_discount_percent}%</span> is permanently
            attached to your account. Choose a continuing plan whenever you're ready —
            no rush.
          </p>
          <a href="/pricing" className="inline-block mt-4 ss-pill ss-pill-primary h-10 leading-10 px-5">
            View plans
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <SubmitForm onSubmitted={refresh} />

        <div>
          <div className="ss-card-premium p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Your messages</p>
                <h3 className="font-heading text-xl text-cocoa mt-1">{visibleMessages.length} on record</h3>
              </div>
              <Inbox size={16} className="text-cocoa/55" />
            </div>
            {visibleMessages.length === 0 ? (
              <p className="text-cocoa/55 text-sm mt-4">Nothing yet. The form on the left starts the conversation.</p>
            ) : (
              <ul className="mt-4 space-y-2" data-testid="amb-msg-list">
                {visibleMessages.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => setActiveMsg(m)}
                      data-testid={`amb-msg-${m.id}`}
                      className={`group w-full text-left bg-ivory border rounded-md p-3 transition-all ${
                        activeMsg?.id === m.id ? "border-mauve" : "border-champagne hover:border-mauve/60"
                      }`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{m.message_type.replaceAll("_"," ")}</p>
                          <p className="text-cocoa font-medium mt-0.5">{m.title}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {m.unread_for_user && <span className="bg-rose w-2 h-2 rounded-full" />}
                          <StatusPill status={m.status} />
                        </div>
                      </div>
                      <p className="text-[11px] text-cocoa/55 font-mono mt-1">{formatDate(m.created_at)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {activeMsg && <MessageThread msg={activeMsg} onChanged={refresh} />}
        </div>
      </div>
    </div>
  );
}
